<template>
  <div class="card" v-loading="loading">
    <div class="card-content">

      <!-- ================= LIST ================= -->
      <div v-if="!editor">
        <div class="msub-menus__head">
          <div>
            <strong>{{ $t('menuSubscriptions.menusTitle') }}</strong>
            <small class="text-muted msub-block">{{ $t('menuSubscriptions.menusHint', { price: pricePerDay + ' ' + currency }) }}</small>
          </div>
          <button class="btn btn-sm btn-purple" @click="openEditor(null)"><i class="fa fa-plus"></i> {{ $t('menuSubscriptions.newMenu') }}</button>
        </div>

        <p v-if="!menus.length && !loading" class="text-muted msub-menus__empty">{{ $t('menuSubscriptions.noMenus') }}</p>

        <div v-for="menu in menus" :key="'menu' + menu.id" class="msub-menu" :class="{ 'is-default': menu.is_default, 'is-wrong': !menu.matches_price }">
          <div class="msub-menu__top">
            <div>
              <a class="msub-menu__name" @click="openEditor(menu)">{{ menu.name }}</a>
              <span v-if="menu.is_default" class="msub-tag msub-tag--default">{{ $t('menuSubscriptions.defaultMenu') }}</span>
              <span v-if="!menu.matches_price" class="msub-tag msub-tag--wrong">{{ $t('menuSubscriptions.totalMismatch', { total: menu.total + ' ' + currency }) }}</span>
              <span v-if="menu.upcoming_days" class="msub-tag">{{ $t('menuSubscriptions.upcomingDays', { count: menu.upcoming_days }) }}</span>
            </div>
            <div class="msub-menu__actions">
              <a v-if="!menu.is_default" class="msub-icon" :title="$t('menuSubscriptions.makeDefault')" @click="setDefault(menu)"><i class="fa fa-star-o"></i></a>
              <a class="msub-icon" :title="$t('menuSubscriptions.duplicate')" @click="duplicate(menu)"><i class="fa fa-files-o"></i></a>
              <a class="msub-icon" :title="$t('general.edit')" @click="openEditor(menu)"><i class="fa fa-pencil"></i></a>
              <a class="msub-icon msub-icon--danger" :title="$t('general.delete')" @click="remove(menu)"><i class="fa fa-trash-o"></i></a>
            </div>
          </div>
          <div class="msub-menu__items">
            <span v-for="(item, index) in menu.items" :key="'mi' + menu.id + '-' + index" class="msub-menu__item">
              <small v-if="item.course" class="text-muted">{{ $t('menuSubscriptions.courses.' + item.course) }}:</small>
              <span :class="{ 'text-danger': item.product_missing }">{{ item.quantity > 1 ? item.quantity + ' x ' : '' }}{{ item.name }}</span>
              <small class="text-muted">({{ item.price }})</small>
            </span>
          </div>
        </div>
      </div>

      <!-- ================= EDITOR ================= -->
      <div v-else>
        <div class="msub-menus__head">
          <strong>{{ editor.id ? $t('menuSubscriptions.editMenu') : $t('menuSubscriptions.newMenu') }}</strong>
          <a class="btn btn-sm btn-default" @click="closeEditor"><i class="fa fa-times"></i> {{ $t('general.cancel') }}</a>
        </div>

        <div v-if="!editor.id && assignDates.length" class="msub-assign-note">
          <i class="fa fa-calendar"></i> {{ $t('menuSubscriptions.willAssignDates', { dates: assignDates.join(', ') }) }}
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t('menuSubscriptions.menuName') }}*</label>
              <input type="text" class="form-control" maxlength="255" v-model="editor.name" :placeholder="$t('menuSubscriptions.menuNamePlaceholder')">
            </div>
          </div>
        </div>

        <!-- product search -->
        <div class="msub-search">
          <label>{{ $t('menuSubscriptions.addProducts') }}</label>
          <div class="msub-search__bar">
            <input type="text" class="form-control" v-model="search.q" :placeholder="$t('menuSubscriptions.searchProducts')" @input="onSearchInput">
            <el-select v-model="search.category_id" clearable filterable :placeholder="$t('menuSubscriptions.allCategories')" @change="searchProducts(1)">
              <el-option v-for="category in categories" :key="category.id" :label="category.name" :value="category.id"></el-option>
            </el-select>
          </div>
          <div class="msub-search__results" v-loading="search.loading">
            <div v-for="product in search.items" :key="'p' + product.id" class="msub-search__row" @click="addProduct(product)">
              <span class="msub-search__name">{{ product.name }} <small class="text-muted">#{{ product.id }}</small></span>
              <small class="text-muted">{{ $t('menuSubscriptions.catalogPrice') }}: {{ product.price }} {{ currency }}<template v-if="product.vat !== null"> · {{ $t('menuSubscriptions.vat') }} {{ product.vat }}%</template></small>
              <a class="msub-icon"><i class="fa fa-plus-circle"></i></a>
            </div>
            <p v-if="!search.loading && !search.items.length" class="text-muted msub-search__empty">{{ $t('menuSubscriptions.noProductsFound') }}</p>
            <div v-if="search.lastPage > 1" class="msub-search__pages">
              <a :class="{ 'is-disabled': search.page <= 1 }" @click="search.page > 1 && searchProducts(search.page - 1)"><i class="fa fa-chevron-left"></i></a>
              <span>{{ search.page }} / {{ search.lastPage }}</span>
              <a :class="{ 'is-disabled': search.page >= search.lastPage }" @click="search.page < search.lastPage && searchProducts(search.page + 1)"><i class="fa fa-chevron-right"></i></a>
            </div>
          </div>
        </div>

        <!-- items -->
        <div class="msub-items">
          <div class="msub-item msub-item--head">
            <span></span>
            <span>{{ $t('menuSubscriptions.course') }}</span>
            <span>{{ $t('menuSubscriptions.product') }}</span>
            <span>{{ $t('menuSubscriptions.quantity') }}</span>
            <span>{{ $t('menuSubscriptions.unitPrice') }}</span>
            <span>{{ $t('menuSubscriptions.lineTotal') }}</span>
            <span></span>
          </div>
          <p v-if="!editor.items.length" class="text-muted msub-items__empty">{{ $t('menuSubscriptions.noItems') }}</p>
          <div v-for="(item, index) in editor.items" :key="'it' + item.key" class="msub-item">
            <span class="msub-item__move">
              <a :class="{ 'is-disabled': index === 0 }" @click="move(index, -1)"><i class="fa fa-chevron-up"></i></a>
              <a :class="{ 'is-disabled': index === editor.items.length - 1 }" @click="move(index, 1)"><i class="fa fa-chevron-down"></i></a>
            </span>
            <span>
              <el-select v-model="item.course" clearable size="small" :placeholder="'-'">
                <el-option v-for="course in courses" :key="course" :label="$t('menuSubscriptions.courses.' + course)" :value="course"></el-option>
              </el-select>
            </span>
            <span>
              <span :class="{ 'text-danger': item.product_missing }">{{ item.name }}</span>
              <small class="text-muted msub-block">
                {{ $t('menuSubscriptions.catalogPriceHint') }}: {{ item.catalog_price !== null && item.catalog_price !== undefined ? item.catalog_price + ' ' + currency : '-' }}<template v-if="item.vat !== null && item.vat !== undefined"> · {{ $t('menuSubscriptions.vat') }} {{ item.vat }}%</template>
              </small>
              <small v-if="item.product_missing" class="text-danger msub-block">{{ $t('menuSubscriptions.productMissing') }}</small>
            </span>
            <span><input type="number" min="1" max="99" step="1" class="form-control input-sm" v-model="item.quantity"></span>
            <span><input type="number" min="0" step="0.01" class="form-control input-sm" v-model="item.price"></span>
            <span class="msub-item__total">{{ lineTotal(item) }}</span>
            <span class="msub-item__actions">
              <a v-if="diffCents !== 0" class="msub-icon" :title="$t('menuSubscriptions.putDifferenceHere')" @click="putDifference(index)"><i class="fa fa-magic"></i></a>
              <a class="msub-icon msub-icon--danger" :title="$t('general.delete')" @click="editor.items.splice(index, 1)"><i class="fa fa-trash-o"></i></a>
            </span>
          </div>
        </div>

        <div class="msub-total" :class="{ 'is-ok': diffCents === 0 && editor.items.length, 'is-wrong': diffCents !== 0 }">
          <div>
            {{ $t('menuSubscriptions.menuTotal') }}: <strong>{{ totalText }} {{ currency }}</strong>
            / {{ $t('menuSubscriptions.pricePerDay') }}: <strong>{{ pricePerDay }} {{ currency }}</strong>
            <span v-if="diffCents !== 0"> · {{ $t('menuSubscriptions.difference') }}: <strong>{{ diffText }} {{ currency }}</strong></span>
            <span v-else-if="editor.items.length"> <i class="fa fa-check"></i></span>
          </div>
          <button v-if="editor.items.length && diffCents !== 0" class="btn btn-sm btn-default" @click="rescale">
            <i class="fa fa-balance-scale"></i> {{ $t('menuSubscriptions.adjustProportionally', { price: pricePerDay }) }}
          </button>
        </div>
        <small class="text-muted msub-block">{{ $t('menuSubscriptions.priceRuleHint') }}</small>

        <div class="text-center mar-to-10">
          <button class="btn btn-purple-light btn-fill btn-wd" :disabled="saving" @click.prevent="saveMenu">{{ $t('general.save') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {Select, Option} from 'element-ui'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'
  import { COURSES, scalePrices, toCents, formatMoney } from './menuSubscriptionHelpers'

  Vue.use(Select)
  Vue.use(Option)

  let itemKey = 0

  export default {
    props: {
      subscription: {
        type: Object,
        required: true
      },
      // dates picked in the calendar: a new menu is opened and assigned to them after saving
      assignDates: {
        type: Array,
        default: () => []
      }
    },
    data () {
      return {
        courses: COURSES,
        loading: false,
        saving: false,
        currency: '',
        pricePerDay: this.subscription.price_per_day,
        menus: [],
        categories: [],
        editor: null,
        search: {
          q: '',
          category_id: null,
          page: 1,
          lastPage: 1,
          items: [],
          loading: false
        },
        searchTimer: null
      }
    },
    computed: {
      totalCents () {
        if (!this.editor) {
          return 0
        }
        return this.editor.items.reduce((total, item) => total + this.lineCents(item), 0)
      },
      diffCents () {
        return toCents(this.pricePerDay) - this.totalCents
      },
      totalText () {
        return formatMoney(this.totalCents)
      },
      diffText () {
        return (this.diffCents > 0 ? '+' : '') + formatMoney(this.diffCents)
      }
    },
    created () {
      this.currency = this.getCurrencySymbol(this.$session.get('selectedCompanyData').currency_iso)
      this.getMenus()
      this.getCategories()
      if (this.assignDates.length) {
        this.openEditor(null)
      }
    },
    beforeDestroy () {
      clearTimeout(this.searchTimer)
    },
    methods: {
      requestData () {
        return {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
      },
      url (path) {
        return '/menu-subscription/' + this.subscription.id + (path || '')
      },
      getMenus () {
        this.loading = true
        this.apiRequest(this.url('/menus'), 'GET', this.requestData(), (response) => {
          this.loading = false
          if (response.status === true) {
            this.applyMenus(response.data)
          }
        })
      },
      applyMenus (data) {
        this.menus = (data && data.menus) || []
        if (data && data.price_per_day !== undefined) {
          this.pricePerDay = data.price_per_day
        }
      },
      getCategories () {
        this.apiRequest('/menu-subscription/categories', 'GET', { ...this.requestData(), location_id: this.subscription.location_token }, (response) => {
          if (response.status === true) {
            this.categories = response.data || []
          }
        })
      },
      /* ---------------- Menu list actions ---------------- */
      setDefault (menu) {
        this.apiRequest(this.url('/menus/' + menu.id + '/default'), 'POST', this.requestData(), (response) => {
          if (response.status === true) {
            this.applyMenus(response.data)
            this.$emit('changed')
          } else {
            swal('', this.firstError(response), 'error').catch(() => {})
          }
        })
      },
      duplicate (menu) {
        this.apiRequest(this.url('/menus/' + menu.id + '/duplicate'), 'POST', this.requestData(), (response) => {
          if (response.status === true) {
            this.getMenus()
            this.$emit('changed')
          } else {
            swal('', this.firstError(response), 'error').catch(() => {})
          }
        })
      },
      remove (menu) {
        let text = this.$t('menuSubscriptions.deleteMenuQuestion', { name: menu.name })
        if (menu.upcoming_days) {
          text += ' ' + this.$t('menuSubscriptions.deleteMenuDays', { count: menu.upcoming_days })
        }
        if (menu.is_default && this.menus.length > 1) {
          text += ' ' + this.$t('menuSubscriptions.deleteMenuDefault')
        }
        swal({ text, type: 'question', showCancelButton: true }).then((result) => {
          if (!result) {
            return
          }
          this.apiRequest(this.url('/menus/' + menu.id), 'DELETE', this.requestData(), (response) => {
            if (response.status === true) {
              this.applyMenus(response.data)
              this.$emit('changed')
            } else {
              swal('', this.firstError(response), 'error').catch(() => {})
            }
          })
        }).catch(() => {})
      },
      /* ---------------- Editor ---------------- */
      openEditor (menu) {
        this.editor = {
          id: menu ? menu.id : null,
          name: menu ? menu.name : '',
          items: menu ? menu.items.map(item => ({
            key: ++itemKey,
            catalogproduct_id: item.catalogproduct_id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            course: item.course,
            catalog_price: item.catalog_price,
            vat: item.vat,
            product_missing: item.product_missing
          })) : []
        }
        this.search.q = ''
        this.searchProducts(1)
      },
      closeEditor () {
        this.editor = null
      },
      onSearchInput () {
        clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => this.searchProducts(1), 300)
      },
      searchProducts (page) {
        const data = {
          ...this.requestData(),
          location_id: this.subscription.location_token,
          page: page || 1
        }
        if (this.search.q.trim()) {
          data.q = this.search.q.trim()
        }
        if (this.search.category_id) {
          data.category_id = this.search.category_id
        }
        this.search.loading = true
        this.apiRequest('/menu-subscription/products', 'GET', data, (response) => {
          this.search.loading = false
          if (response.status === true && response.data) {
            this.search.items = response.data.items || []
            this.search.page = response.data.pagination.current_page
            this.search.lastPage = response.data.pagination.last_page
          } else {
            this.search.items = []
          }
        })
      },
      addProduct (product) {
        this.editor.items.push({
          key: ++itemKey,
          catalogproduct_id: product.id,
          name: product.name,
          quantity: 1,
          // the catalog price is only a starting point: adjust it so the menu adds up to the price per day
          price: product.price !== null && product.price !== undefined ? parseFloat(product.price).toFixed(2) : '0.00',
          course: null,
          catalog_price: product.price,
          vat: product.vat,
          product_missing: false
        })
      },
      move (index, step) {
        const target = index + step
        if (target < 0 || target >= this.editor.items.length) {
          return
        }
        const items = this.editor.items
        items.splice(target, 0, items.splice(index, 1)[0])
      },
      lineCents (item) {
        const quantity = Math.max(0, parseInt(item.quantity, 10) || 0)
        return toCents(item.price) * quantity
      },
      lineTotal (item) {
        return formatMoney(this.lineCents(item))
      },
      // put the whole remaining difference on one line (when its quantity divides it)
      putDifference (index) {
        const item = this.editor.items[index]
        const quantity = Math.max(1, parseInt(item.quantity, 10) || 1)
        const newUnit = toCents(item.price) + this.diffCents / quantity
        if (this.diffCents % quantity !== 0 || newUnit < 0) {
          swal('', this.$t('menuSubscriptions.cannotPutDifference'), 'info').catch(() => {})
          return
        }
        item.price = formatMoney(newUnit)
      },
      rescale () {
        const prices = scalePrices(this.editor.items, this.pricePerDay)
        if (!prices) {
          swal('', this.$t('menuSubscriptions.cannotRescale'), 'info').catch(() => {})
          return
        }
        this.editor.items.forEach((item, i) => {
          item.price = prices[i]
        })
      },
      saveMenu () {
        if (!(this.editor.name || '').trim()) {
          swal('', this.$t('menuSubscriptions.menuNameRequired'), 'warning').catch(() => {})
          return
        }
        if (!this.editor.items.length) {
          swal('', this.$t('menuSubscriptions.noItems'), 'warning').catch(() => {})
          return
        }
        if (this.diffCents !== 0) {
          swal('', this.$t('menuSubscriptions.totalMustMatch', { total: this.totalText, price: this.pricePerDay }), 'warning').catch(() => {})
          return
        }
        const data = {
          ...this.requestData(),
          name: this.editor.name,
          items: this.editor.items.map(item => ({
            catalogproduct_id: item.catalogproduct_id,
            quantity: String(item.quantity),
            price: item.price,
            course: item.course || null
          }))
        }
        const isNew = !this.editor.id
        this.saving = true
        this.apiRequest(isNew ? this.url('/menus') : this.url('/menus/' + this.editor.id), isNew ? 'POST' : 'PUT', data, (response) => {
          if (response.status !== true) {
            this.saving = false
            swal('', this.firstError(response), 'error').catch(() => {})
            return
          }
          this.applyMenus(response.data.menus)
          this.$emit('changed')

          if (isNew && this.assignDates.length) {
            this.assignToDates(response.data.id)
            return
          }
          this.saving = false
          this.editor = null
          swal({ title: '', text: this.$t('menuSubscriptions.menuSaved'), type: 'success', showConfirmButton: false, timer: 1200 }).catch(() => {})
        })
      },
      assignToDates (menuId) {
        const data = { ...this.requestData(), dates: this.assignDates, menu_id: menuId }
        this.apiRequest(this.url('/calendar'), 'PUT', data, (response) => {
          this.saving = false
          this.editor = null
          if (response.status === true) {
            swal({ title: '', text: this.$t('menuSubscriptions.menuSavedAssigned'), type: 'success', showConfirmButton: false, timer: 1500 }).catch(() => {})
            this.$emit('assigned')
          } else {
            swal('', this.firstError(response), 'error').catch(() => {})
          }
        })
      },
      firstError (response) {
        const errors = (response && response.errors) || {}
        const key = Object.keys(errors)[0]
        if (!key) {
          return this.$t('validations.wentWrong')
        }
        return Array.isArray(errors[key]) ? errors[key][0] : errors[key]
      }
    }
  }
</script>
<style>
  .msub-menus__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;
  }
  .msub-menus__empty {
    padding: 20px 0;
    text-align: center;
  }
  .msub-menu {
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 10px;
  }
  .msub-menu.is-default {
    border-color: #b39ddb;
    background: #faf8fe;
  }
  .msub-menu.is-wrong {
    border-color: #ffcc80;
  }
  .msub-menu__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }
  .msub-menu__name {
    cursor: pointer;
    font-weight: 600;
    color: inherit;
    margin-right: 6px;
  }
  .msub-menu__actions {
    display: flex;
    gap: 12px;
    font-size: 15px;
  }
  .msub-menu__items {
    margin-top: 6px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    font-size: 13px;
  }
  .msub-tag {
    display: inline-block;
    margin-right: 4px;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 11px;
    background: #f5f5f5;
    color: #777;
    border: 1px solid #e0e0e0;
  }
  .msub-tag--default {
    background: #ede7f6;
    color: #5e35b1;
    border-color: #d1c4e9;
  }
  .msub-tag--wrong {
    background: #fff3e0;
    color: #e65100;
    border-color: #ffcc80;
  }
  .msub-assign-note {
    background: #e3f2fd;
    border: 1px solid #bbdefb;
    color: #0d47a1;
    border-radius: 6px;
    padding: 6px 10px;
    margin-bottom: 10px;
    font-size: 13px;
  }
  .msub-search__bar {
    display: flex;
    gap: 8px;
  }
  .msub-search__bar .el-select {
    min-width: 220px;
  }
  .msub-search__results {
    border: 1px solid #eee;
    border-radius: 6px;
    max-height: 240px;
    overflow-y: auto;
    margin-top: 6px;
    min-height: 60px;
  }
  .msub-search__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-bottom: 1px solid #f5f5f5;
    cursor: pointer;
  }
  .msub-search__row:hover {
    background: #f6f2fc;
  }
  .msub-search__name {
    flex: 1;
  }
  .msub-search__empty {
    padding: 10px;
    margin: 0;
  }
  .msub-search__pages {
    display: flex;
    justify-content: center;
    gap: 12px;
    padding: 6px;
  }
  .msub-search__pages a,
  .msub-item__move a {
    cursor: pointer;
  }
  .msub-search__pages a.is-disabled,
  .msub-item__move a.is-disabled {
    opacity: 0.3;
    cursor: default;
  }
  .msub-items {
    margin-top: 14px;
  }
  .msub-items__empty {
    padding: 10px 0;
  }
  .msub-item {
    display: grid;
    grid-template-columns: 24px 130px minmax(180px, 1fr) 80px 110px 90px 60px;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid #f3f3f3;
  }
  .msub-item--head {
    font-size: 12px;
    color: #999;
  }
  .msub-item__move {
    display: flex;
    flex-direction: column;
    font-size: 11px;
  }
  .msub-item__total {
    font-weight: 600;
  }
  .msub-item__actions {
    display: flex;
    gap: 10px;
  }
  .msub-total {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #f5f5f5;
  }
  .msub-total.is-ok {
    background: #e8f5e9;
    color: #2e7d32;
  }
  .msub-total.is-wrong {
    background: #fff3e0;
    color: #e65100;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/MenuSubscriptions/MenuSubscriptionMenus.vue